import React, { useState, useEffect, useRef, useCallback } from 'react';
import { activityService } from '../services/activityService';
import { downloadIcsEvent, buildGoogleCalendarUrl, parseAppointmentStart } from '../utils/calendarIcs';
import { loadSavedSessions, saveSession, deleteSessionById } from './CallSession';
import { CAR_OUTCOMES } from '../constants/callOutcomes';
import { useCallOutcomeFromNotes } from '../hooks/useCallOutcomeFromNotes';
import { CallOutcomeThinking, CallOutcomeAmbiguousHint } from './CallOutcomeThinking';

function nowDate() {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}
function nowTime() {
  return new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function fmtSessionDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) +
      ' · ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function resolveSessionLeads(session, allLeads) {
  return (session.leadsSnapshot || []).map(l => allLeads.find(al => String(al.id) === String(l.id)) || l);
}

/* ── Session-Hub: pausierte Call Sessions vom Desktop ── */
function CarSessionHub({ sessions, allLeads, onResume, onExit, onRefresh }) {
  const active = sessions.filter(s => (s.leadsSnapshot?.length || 0) > 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: '#f7faf7' }}>
      <div style={{ padding: '16px 20px', background: '#fff', borderBottom: '1px solid #e2ebe3', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#7576c9" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><path d="M14.31 8l5.74 9.94M9.69 8h11.48M7.38 12l5.74-9.94M9.69 16L3.95 6.06M14.31 16H2.83M16.62 12l-5.74 9.94"/>
          </svg>
          <span style={{ fontSize: 20, fontWeight: 800, color: '#22302a', flex: 1 }}>Fahrmodus</span>
          <button type="button" onClick={onExit}
            style={{ padding: '8px 14px', borderRadius: 10, background: '#f0f5f0', border: '1px solid #e2ebe3', color: '#66796c', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Schließen
          </button>
        </div>
        <p style={{ fontSize: 13, color: '#66796c', margin: 0, lineHeight: 1.45 }}>
          Setze am Computer eine Call Session fort – dieselben Leads und der gleiche Fortschritt.
        </p>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px' }}>
        {active.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px', color: '#93a89a' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ margin: '0 auto 16px', opacity: 0.4 }}>
              <path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 0112 18.92a19.5 19.5 0 01-6-6A19.79 19.79 0 012.08 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
            </svg>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#4a5a4f', marginBottom: 8 }}>Keine pausierte Session</div>
            <p style={{ fontSize: 14, margin: 0, lineHeight: 1.5 }}>
              Am Desktop: Leads wählen → Call Session starten → pausieren (✕). Danach erscheint die Session hier.
            </p>
            <button type="button" onClick={onRefresh}
              style={{ marginTop: 20, height: 40, padding: '0 18px', borderRadius: 10, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4f46e5', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Erneut prüfen
            </button>
          </div>
        ) : active.map(session => {
          const total = session.leadsSnapshot?.length || 0;
          const done = session.done?.length || 0;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          const resumeLeads = resolveSessionLeads(session, allLeads);
          const cur = resumeLeads[session.index || 0];

          return (
            <button
              key={session.id}
              type="button"
              onClick={() => onResume(session, resumeLeads)}
              style={{
                width: '100%', textAlign: 'left', marginBottom: 12, padding: '16px 18px',
                borderRadius: 14, border: '1.5px solid #e2ebe3', background: '#fff', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(15,23,42,0.05)', transition: 'border-color .15s, box-shadow .15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#7576c9,#8f7cc9)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 0112 18.92a19.5 19.5 0 01-6-6A19.79 19.79 0 012.08 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#22302a', marginBottom: 4 }}>{session.name}</div>
                  <div style={{ fontSize: 12, color: '#66796c', marginBottom: 10 }}>
                    {fmtSessionDate(session.updatedAt || session.createdAt)}
                    {cur && <> · Weiter bei <strong style={{ color: '#4f46e5' }}>{cur.name}</strong></>}
                  </div>
                  <div style={{ height: 6, background: '#f0f5f0', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg,#22c55e,#16a34a)', borderRadius: 3 }} />
                  </div>
                  <div style={{ fontSize: 12, color: '#66796c' }}>
                    Lead {(session.index || 0) + 1}/{total} · {done} erledigt
                  </div>
                </div>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7576c9" strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 4 }}>
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Fortschritts-Ring: erledigte Leads in der Session ── */
function ProgressRing({ done, total, size = 42 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(1, done / total) : 0;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2ebe3" strokeWidth="4"/>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#22c55e" strokeWidth="4"
        strokeDasharray={`${circ * pct} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray .45s cubic-bezier(0.4,0,0.2,1)' }}/>
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" fontSize="11" fontWeight="800" fill="#37453b">{done}</text>
    </svg>
  );
}

/* ── Aktiver Lead in der Session ── */
function CarLeadDetail({
  lead,
  sessionLeads,
  index,
  doneSet,
  bestandskunden,
  statusMeta,
  currentUser,
  sessionName,
  onPause,
  onPrev,
  onNext,
  onMarkDone,
  onWorked,
}) {
  const [notes, setNotes] = useState('');
  const [pickedOutcome, setPickedOutcome] = useState(null);
  const [saved, setSaved] = useState(false);
  const [userPickedOutcome, setUserPickedOutcome] = useState(false);
  const touchStart = useRef(null);

  const sm = statusMeta[lead.status] || { label: lead.status, color: '#93a89a' };
  const hasTel = lead.tel && lead.tel !== '–';
  const hasNext = index < sessionLeads.length - 1;
  const hasPrev = index > 0;
  const isDone = doneSet.has(lead.id);

  const {
    inferring,
    inferLabel,
    showAmbiguous,
    highlightSlug,
    onNotesBlur,
    dismissAmbiguous,
    resetAnalysis,
  } = useCallOutcomeFromNotes({
    currentOutcome: pickedOutcome,
    setOutcome: (slug) => { setPickedOutcome(slug); setUserPickedOutcome(false); },
    leadName: lead.name,
    company: lead.company,
    skipIfOutcomeChosen: userPickedOutcome,
  });

  const matches = (lead.branch
    ? bestandskunden.filter(k => (k.branch || '').toLowerCase().trim() === (lead.branch || '').toLowerCase().trim())
    : []
  ).slice(0, 3);

  const displayOutcome = pickedOutcome || highlightSlug;
  const selectedOc = CAR_OUTCOMES.find(o => o.id === (saved ? pickedOutcome : displayOutcome));

  function persistCall(oc) {
    const act = {
      id: Date.now(),
      type: 'call',
      outcomeSaved: true,
      outcome: oc.id,
      outcomeLabel: oc.label,
      title: `Anruf – ${oc.label}`,
      date: nowDate(),
      time: nowTime(),
      ts: new Date().toISOString(),
      by: currentUser?.name || currentUser?.email || 'Nutzer',
      byUserId: currentUser?.id || null,
      leadId: lead.id,
      leadName: lead.name || lead.company || null,
      note: notes.trim() || null,
    };
    activityService.addToLead(lead.id, act);
    onWorked?.(lead.id);
    setSaved(true);
    setPickedOutcome(oc.id);
  }

  function handlePickOutcome(oc) {
    setUserPickedOutcome(true);
    persistCall(oc);
  }

  function handleNextAfterSave() {
    setSaved(false);
    setNotes('');
    setPickedOutcome(null);
    setUserPickedOutcome(false);
    resetAnalysis();
    onNext();
  }

  /* Horizontaler Swipe wechselt den Lead — vertikales Scrollen bleibt unberührt */
  function handleTouchStart(e) {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  }
  function handleTouchEnd(e) {
    const s = touchStart.current;
    touchStart.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    const isSwipe = Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.8 && Date.now() - s.at < 600;
    if (!isSwipe) return;
    if (dx < 0 && hasNext) onNext();
    else if (dx > 0 && hasPrev) onPrev();
  }

  const doneCount = doneSet.size;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, background: '#fff' }}>
      {/* Session-Leiste */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid #e2ebe3', background: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" onClick={onPause}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7576c9', fontSize: 13, fontWeight: 600, padding: '4px 0', flexShrink: 0 }}>
            ← Sessions
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#37453b' }}>Lead {index + 1} / {sessionLeads.length}</div>
            <div style={{ fontSize: 11, color: '#93a89a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {doneCount} erledigt · {sessionName}
            </div>
          </div>
          <ProgressRing done={doneCount} total={sessionLeads.length}/>
          <button type="button" onClick={onPause}
            style={{ background: '#f0f5f0', border: '1px solid #e2ebe3', borderRadius: 8, padding: '4px 10px', fontSize: 12, color: '#66796c', cursor: 'pointer', flexShrink: 0 }}>
            Pause
          </button>
        </div>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 8 }}>
          {sessionLeads.map((l, i) => (
            <div
              key={l.id}
              style={{
                height: 6,
                flex: i === index ? 2 : 1,
                maxWidth: i === index ? 32 : 12,
                borderRadius: 3,
                background: doneSet.has(l.id) ? '#22c55e' : i === index ? '#7576c9' : '#e2ebe3',
                transition: 'all .2s',
              }}
            />
          ))}
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
        onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        <div style={{ padding: '14px 18px 8px', textAlign: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: sm.color, background: sm.color + '14', padding: '2px 10px', borderRadius: 99 }}>{sm.label}</span>
          <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginTop: 8, lineHeight: 1.15 }}>{lead.name}</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#4a5a4f', marginTop: 4 }}>{lead.company}</div>
        </div>

        {matches.length > 0 && (
          <div style={{ margin: '0 16px 10px', padding: '10px 12px', background: '#f7faf7', borderRadius: 10, border: '1px solid #f0f5f0' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#7576c9', marginBottom: 6 }}>REFERENZ · {lead.branch}</div>
            {matches.map((k, i) => (
              <div key={i} style={{ fontSize: 12, color: '#4a5a4f', marginBottom: 4 }}>{k.company || k.name}</div>
            ))}
          </div>
        )}

        <div style={{ padding: '8px 16px' }}>
          {hasTel ? (
            <a href={`tel:${String(lead.tel).replace(/\s/g, '')}`}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '14px 0', borderRadius: 14, background: 'linear-gradient(135deg,#22c55e,#16a34a)', color: '#fff', textDecoration: 'none', boxShadow: '0 6px 20px rgba(34,197,94,0.28)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 0112 18.92a19.5 19.5 0 01-6-6A19.79 19.79 0 012.08 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Anrufen</div>
                <div style={{ fontSize: 14, opacity: 0.9 }}>{lead.tel}</div>
              </div>
            </a>
          ) : (
            <div style={{ padding: '14px', textAlign: 'center', color: '#93a89a', background: '#f7faf7', borderRadius: 12 }}>Keine Telefonnummer</div>
          )}
        </div>

        {/* Notizen */}
        <div style={{ padding: '0 16px 12px' }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#93a89a', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
            Notiz zum Gespräch
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onBlur={() => onNotesBlur(notes)}
            placeholder="Kurz festhalten – beim Verlassen des Feldes wird das Ergebnis vorgeschlagen…"
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box', borderRadius: 12, border: '1.5px solid #e2ebe3',
              padding: '12px 14px', fontSize: 15, lineHeight: 1.5, resize: 'vertical', minHeight: 88,
              fontFamily: 'inherit', outline: 'none', background: '#fafafa',
            }}
            onFocus={e => { e.target.style.borderColor = '#7576c9'; }}
          />
        </div>

        {inferring && <div style={{ padding: '0 16px' }}><CallOutcomeThinking label={inferLabel} /></div>}

        {/* Ergebnis */}
        <div style={{ padding: '0 16px 16px' }}>
          {!saved ? (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#93a89a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, textAlign: 'center' }}>
                Ergebnis
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {CAR_OUTCOMES.map(oc => {
                  const active = displayOutcome === oc.id;
                  const flash = highlightSlug === oc.id;
                  return (
                    <button
                      key={oc.id}
                      type="button"
                      onClick={() => handlePickOutcome(oc)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7, padding: '10px 11px', borderRadius: 10,
                        background: active || flash ? oc.color + '14' : '#f7faf7',
                        border: `1.5px solid ${active || flash ? oc.color + '55' : '#f0f5f0'}`,
                        cursor: 'pointer', textAlign: 'left',
                        boxShadow: flash ? `0 0 0 2px ${oc.color}40` : 'none',
                        transition: 'all .2s',
                      }}
                    >
                      <span style={{ fontSize: 16 }}>{oc.emoji}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#22302a', lineHeight: 1.3 }}>{oc.label.replace('Erreicht – ', '').replace('Nicht erreicht – ', '')}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderRadius: 12, background: (selectedOc?.color || '#16a34a') + '12', border: `1.5px solid ${(selectedOc?.color || '#16a34a')}35` }}>
                <span style={{ fontSize: 22 }}>{selectedOc?.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: selectedOc?.color }}>Gespeichert</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#22302a' }}>{selectedOc?.label}</div>
                </div>
                <button type="button" onClick={() => { setSaved(false); setPickedOutcome(null); }}
                  style={{ fontSize: 12, color: '#93a89a', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Ändern
                </button>
              </div>
              {pickedOutcome === 'reached_appt' && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button type="button" onClick={() => {
                    const start = parseAppointmentStart({ dateIso: new Date().toISOString().slice(0, 10), time: '09:00' });
                    downloadIcsEvent({ title: `Termin: ${lead.name}`, description: notes || '', location: lead.city !== '–' ? lead.city : '', start, leadName: lead.name });
                  }}
                    style={{ height: 34, padding: '0 12px', borderRadius: 8, border: '1px solid #86efac', background: '#fff', color: '#15803d', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    .ics / Outlook
                  </button>
                  <button type="button" onClick={() => {
                    const start = parseAppointmentStart({ dateIso: new Date().toISOString().slice(0, 10), time: '09:00' });
                    window.open(buildGoogleCalendarUrl({ title: `Termin: ${lead.name}`, description: notes || '', location: lead.city !== '–' ? lead.city : '', start }), '_blank');
                  }}
                    style={{ height: 34, padding: '0 12px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Google
                  </button>
                </div>
              )}
              {hasNext && (
                <button type="button" onClick={handleNextAfterSave}
                  style={{ width: '100%', marginTop: 12, padding: '15px 0', borderRadius: 14, background: '#7576c9', border: 'none', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
                  Nächster Lead →
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {showAmbiguous && (
        <div style={{ padding: '0 16px 8px' }}>
          <CallOutcomeAmbiguousHint onDismiss={dismissAmbiguous} />
        </div>
      )}

      {/* Bottom-Action-Bar — Daumenreichweite */}
      <div style={{ flexShrink: 0, display: 'flex', gap: 8, padding: '10px 14px', paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))', borderTop: '1px solid #e2ebe3', background: '#fff' }}>
        <button type="button" disabled={!hasPrev} onClick={onPrev}
          style={{ width: 54, height: 50, borderRadius: 12, border: '1px solid #e2ebe3', background: '#f7faf7', color: hasPrev ? '#4a5a4f' : '#cbd8cd', cursor: hasPrev ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <button type="button" onClick={onMarkDone}
          style={{ flex: 1, height: 50, borderRadius: 12, border: 'none', background: isDone ? 'linear-gradient(135deg,#15803d,#16a34a)' : 'linear-gradient(135deg,#ea580c,#f97316)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px rgba(249,115,22,0.28)' }}>
          {hasNext ? 'Fertig – Nächster →' : 'Session beenden'}
        </button>
        <button type="button" disabled={!hasNext} onClick={onNext}
          style={{ width: 54, height: 50, borderRadius: 12, border: '1px solid #e2ebe3', background: '#f7faf7', color: hasNext ? '#4a5a4f' : '#cbd8cd', cursor: hasNext ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  );
}

/* ── Session-Runner ── */
function CarSessionRunner({
  sessionId,
  sessionName,
  leads,
  startIndex,
  initDone,
  bestandskunden,
  statusMeta,
  currentUser,
  onPause,
  onSessionComplete,
  onFinish,
}) {
  const [index, setIndex] = useState(startIndex);
  const [done, setDone] = useState(() => new Set(initDone || []));

  useEffect(() => {
    const existing = loadSavedSessions().find(s => s.id === sessionId);
    saveSession({
      id: sessionId,
      name: existing?.name || sessionName,
      createdAt: existing?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      leadIds: leads.map(l => l.id),
      leadsSnapshot: leads,
      index,
      done: [...done],
      total: leads.length,
    });
  }, [index, done, sessionId, sessionName, leads]);

  const current = leads[index];
  if (!current) {
    onFinish();
    return null;
  }

  function goNext() {
    if (index < leads.length - 1) setIndex(i => i + 1);
    else finishAll();
  }

  function finishAll() {
    deleteSessionById(sessionId);
    onSessionComplete?.({ excludeLeadIds: leads.map(l => l.id), source: 'car_mode' });
    onFinish();
  }

  function markDoneAndNext() {
    setDone(prev => new Set([...prev, current.id]));
    goNext();
  }

  return (
    <CarLeadDetail
      key={current.id}
      lead={current}
      sessionLeads={leads}
      index={index}
      doneSet={done}
      bestandskunden={bestandskunden}
      statusMeta={statusMeta}
      currentUser={currentUser}
      sessionName={sessionName}
      onPause={onPause}
      onPrev={() => setIndex(i => Math.max(0, i - 1))}
      onNext={goNext}
      onMarkDone={markDoneAndNext}
      onWorked={id => setDone(prev => new Set([...prev, id]))}
    />
  );
}

export default function CarModeView({ leads, bestandskunden, statusMeta, currentUser, onExit, onSessionComplete, initialSession }) {
  const [activeSession, setActiveSession] = useState(() => {
    if (!initialSession) return null;
    return {
      sessionId: initialSession.session.id,
      sessionName: 'Call Session – ' + new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      leads: initialSession.resumeLeads,
      startIndex: initialSession.session.index || 0,
      initDone: initialSession.session.done || [],
    };
  });
  const [sessions, setSessions] = useState(() => loadSavedSessions());

  const refreshSessions = useCallback(() => {
    setSessions(loadSavedSessions());
  }, []);

  // Live-Refresh, wenn Sessions von einem anderen Gerät gesynct werden
  useEffect(() => {
    window.addEventListener('crm-sessions-updated', refreshSessions);
    return () => window.removeEventListener('crm-sessions-updated', refreshSessions);
  }, [refreshSessions]);

  function handleResume(session, resumeLeads) {
    if (!resumeLeads.length) return;
    setActiveSession({
      sessionId: session.id,
      sessionName: session.name,
      leads: resumeLeads,
      startIndex: Math.min(session.index || 0, resumeLeads.length - 1),
      initDone: session.done || [],
    });
  }

  function handlePause() {
    setActiveSession(null);
    if (initialSession) { onExit?.(); return; }
    refreshSessions();
  }

  function handleFinish() {
    setActiveSession(null);
    if (initialSession) { onExit?.(); return; }
    refreshSessions();
  }

  return (
    <div
      className="crm-car-mode-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        background: '#f7faf7',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {activeSession ? (
          <CarSessionRunner
            sessionId={activeSession.sessionId}
            sessionName={activeSession.sessionName}
            leads={activeSession.leads}
            startIndex={activeSession.startIndex}
            initDone={activeSession.initDone}
            bestandskunden={bestandskunden}
            statusMeta={statusMeta}
            currentUser={currentUser}
            onPause={handlePause}
            onSessionComplete={onSessionComplete}
            onFinish={handleFinish}
          />
        ) : !initialSession ? (
          <CarSessionHub
            sessions={sessions}
            allLeads={leads}
            onResume={handleResume}
            onExit={onExit}
            onRefresh={refreshSessions}
          />
        ) : null}
      </div>
    </div>
  );
}
